import { useMemo, useState, useEffect } from "react";
import { functions } from "../firebaseConfig";
import { httpsCallable } from "firebase/functions";
import { Eye, EyeOff } from "lucide-react";

import Header from "../components/HeaderV2";
import common from "../style/screens/CommonV2.module.css";
import style from "../style/screens/PartnerV2.module.css";
import ErrMsg from "../ErrMsg";
import { BlockInput } from "../components/Inputs";
import { PartnerType, usePartner } from "../PartnerContext";

const EMAIL_PATTERN = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/;

const Partner = () => {
    const { partner } = usePartner();
    const [addPartner, setAddPartner] = useState<boolean>(false);

    const latestPartner = useMemo(() => {
        return [...partner].sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0))[0];
    }, [partner]);

    return (
        <>
            <Header />
            <div className={common["container"]}>
                <div className={common["wrapper"]}>
                    <h4 className={common["text--title"]}>파트너사 관리</h4>
                    <p className={common["text--copy"]}>
                        파트너사를 등록하고, 파트너사 최고 관리자 계정을 함께 생성합니다.
                    </p>

                    <div className={style["summary-grid"]}>
                        <SummaryCard label="전체 파트너사" value={`${partner.length}개`} />
                        <SummaryCard label="최근 등록" value={latestPartner?.name || "-"} subValue={latestPartner?.id} />
                        <SummaryCard label="생성 방식" value="파트너사 + 관리자" subValue="최고 관리자 계정 동시 생성" />
                    </div>

                    <PartnerTable openCreate={() => setAddPartner(true)} />
                </div>
            </div>

            {addPartner && <AddPartner close={() => setAddPartner(false)} />}
        </>
    );
};

const SummaryCard = ({ label, value, subValue }: { label: string; value: string; subValue?: string }) => (
    <div className={style["summary-card"]}>
        <p>{label}</p>
        <strong>{value}</strong>
        {subValue && <span>{subValue}</span>}
    </div>
);

type EmailCheckStatus = "idle" | "checking" | "available" | "unavailable" | "stale";

const AddPartner = ({ close }: { close: () => void }) => {
    const [partnerId, setPartnerId] = useState<string>("");
    const [partnerIdError, setPartnerIdError] = useState<string>("");

    useEffect(() => {
        if (partnerId && partnerId.length < 3) {
            setPartnerIdError(ErrMsg["auth/partnerId-invalid"]);
        } else {
            setPartnerIdError("");
        }
    }, [partnerId]);

    const [partnerName, setPartnerName] = useState<string>("");
    const [partnerNameError, setPartnerNameError] = useState<string>("");

    useEffect(() => {
        if (partnerName && partnerName.length < 2) {
            setPartnerNameError(ErrMsg["auth/partnerName-invalid"]);
        } else {
            setPartnerNameError("");
        }
    }, [partnerName]);

    const [name, setName] = useState<string>("");
    const [nameError, setNameError] = useState<string>("");

    useEffect(() => {
        if (name && name.length < 2) {
            setNameError(ErrMsg["auth/name-invalid"]);
        } else {
            setNameError("");
        }
    }, [name]);

    const [email, setEmail] = useState<string>("");
    const [emailError, setEmailError] = useState<string>("");
    const [emailCheckStatus, setEmailCheckStatus] = useState<EmailCheckStatus>("idle");
    const [emailCheckMessage, setEmailCheckMessage] = useState<string>("");
    const [checkedEmail, setCheckedEmail] = useState<string>("");

    useEffect(() => {
        if (email && !EMAIL_PATTERN.test(email)) {
            setEmailError(ErrMsg["auth/email-invalid"]);
            setEmailCheckStatus("idle");
            setEmailCheckMessage("");
            setCheckedEmail("");
        } else {
            setEmailError("");
        }

        if (!email) {
            setEmailCheckStatus("idle");
            setEmailCheckMessage("");
            setCheckedEmail("");
            return;
        }

        if (checkedEmail && email !== checkedEmail) {
            setEmailCheckStatus("stale");
            setEmailCheckMessage("이메일이 변경되었습니다. 중복체크를 다시 진행해주세요.");
        }
    }, [email, checkedEmail]);

    const checkEmail = () => {
        const nextEmail = email.trim();
        if (!nextEmail) {
            setEmailError(ErrMsg["auth/email-empty"]);
            return;
        }
        if (!EMAIL_PATTERN.test(nextEmail)) {
            setEmailError(ErrMsg["auth/email-invalid"]);
            return;
        }

        if (nextEmail !== email) {
            setEmail(nextEmail);
        }

        setEmailError("");
        setEmailCheckStatus("checking");
        setEmailCheckMessage("이메일 중복 확인 중입니다.");

        return httpsCallable(
            functions,
            "adminEmailCheck"
        )({ email: nextEmail })
            .then((result) => {
                const data = result.data as {
                    code?: boolean;
                    available?: boolean;
                    message?: string;
                };

                setCheckedEmail(nextEmail);

                if (data.available) {
                    setEmailCheckStatus("available");
                    setEmailCheckMessage("사용 가능한 이메일입니다.");
                } else {
                    setEmailCheckStatus("unavailable");
                    setEmailCheckMessage(ErrMsg[data.message || "auth/email-already-in-use"]);
                }
            })
            .catch((err) => {
                console.error(err.message);
                setCheckedEmail("");
                setEmailCheckStatus("unavailable");
                setEmailCheckMessage("중복 확인에 실패했습니다. 다시 시도해주세요.");
            });
    };

    const [password, setPassword] = useState<string>("");
    const [passwordError, setPasswordError] = useState<string>("");
    const [showPassword, setShowPassword] = useState<boolean>(false);

    useEffect(() => {
        if (password && password.length < 6) {
            setPasswordError(ErrMsg["auth/password-invalid"]);
        } else {
            setPasswordError("");
        }
    }, [password]);

    const [confirm, setConfirm] = useState<string>("");
    const [confirmError, setConfirmError] = useState<string>("");
    const [showConfirm, setShowConfirm] = useState<boolean>(false);

    useEffect(() => {
        if (confirm && confirm !== password) {
            setConfirmError(ErrMsg["auth/password-not-match"]);
        } else {
            setConfirmError("");
        }
    }, [confirm, password]);

    const [loading, setLoading] = useState<boolean>(false);

    const createPartner = async () => {
        if (nameError || emailError || passwordError || confirmError) {
            return;
        }

        if (!partnerId) {
            setPartnerIdError(ErrMsg["auth/partnerId-empty"]);
            return;
        } else if (!partnerName) {
            setPartnerNameError(ErrMsg["auth/partnerName-empty"]);
            return;
        } else if (!name) {
            setNameError(ErrMsg["auth/name-empty"]);
            return;
        } else if (!email) {
            setEmailError(ErrMsg["auth/email-empty"]);
            return;
        } else if (emailCheckStatus === "checking") {
            setEmailCheckMessage("이메일 중복 확인이 끝난 뒤 등록해주세요.");
            return;
        } else if (emailCheckStatus !== "available" || checkedEmail !== email) {
            setEmailCheckStatus(checkedEmail ? "stale" : "unavailable");
            setEmailCheckMessage(checkedEmail ? "이메일이 변경되었습니다. 중복체크를 다시 진행해주세요." : "관리자 이메일 중복체크를 진행해주세요.");
            return;
        } else if (!password) {
            setPasswordError(ErrMsg["auth/password-empty"]);
            return;
        } else if (!confirm) {
            setConfirmError(ErrMsg["auth/password-empty"]);
            return;
        }

        setLoading(true);

        return httpsCallable(
            functions,
            "partnerCreate"
        )({
            partner: {
                partnerId: partnerId,
                name: partnerName,
            },
            admin: {
                name: name,
                type: "GROUP_ADMIN",
                email: email,
                password: password,
                confirm: confirm,
                partnerId: partnerId,
            },
        })
            .then((result) => {
                setLoading(false);

                const data = result.data as {
                    code?: string;
                    message?: string;
                };

                if (data.code) {
                    hide();
                }
            })
            .catch((err) => {
                console.error(err.message);
                setLoading(false);
                switch (err.message) {
                    case "auth/name-invalid":
                    case "auth/name-weak":
                        setNameError(ErrMsg[err.message]);
                        break;
                    case "auth/partnerId-invalid":
                    case "auth/partnerId-already-in-use":
                    case "auth/partnerId-weak":
                        setPartnerIdError(ErrMsg[err.message]);
                        break;
                    case "auth/partnerName-invalid":
                    case "auth/partnerName-already-in-use":
                        setPartnerNameError(ErrMsg[err.message]);
                        break;
                    case "auth/email-invalid":
                    case "auth/email-already-in-use":
                        setEmailError(ErrMsg[err.message]);
                        break;
                    case "auth/password-invalid":
                    case "auth/password-weak":
                        setPasswordError(ErrMsg[err.message]);
                        break;
                    case "auth/password-not-match":
                        setConfirmError(ErrMsg[err.message]);
                        break;
                    case "auth/not-super-admin":
                        alert("최고 관리자만 생성이 가능합니다");
                        break;
                }

                return false;
            });
    };

    const [active, setActive] = useState<boolean>(false);
    useEffect(() => {
        setActive(true);
    }, []);
    const hide = () => {
        setActive(false);
        setTimeout(() => {
            close();
        }, 520);
    };

    return (
        <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
            <div className={[common["unit--overlay"], style["modal"], active && common["unit--overlay--active"]].join(" ")}>
                <div className={style["modal-header"]}>
                    <div>
                        <p className={style["section-kicker"]}>파트너사 등록</p>
                        <h4 className={style["modal-title"]}>파트너사 추가</h4>
                        <p className={style["modal-copy"]}>파트너사 정보와 파트너사 관리자 계정을 함께 생성합니다.</p>
                    </div>
                    <button className={style["icon-button"]} onClick={hide} aria-label="닫기">
                        ×
                    </button>
                </div>

                <div className={common["wrapper--overlay"]}>
                    <div className={style["modal-section"]}>
                        <div className={style["section-head"]}>
                            <h5>파트너사 기본 정보</h5>
                            <p>아이디는 3자 이상이며, 파트너사 이름과 함께 기존 등록 정보와 중복될 수 없습니다.</p>
                        </div>
                        <div className={style["unit--overlay--inputs"]}>
                            <BlockInput
                                type="text"
                                placeholder="파트너사 아이디"
                                value={partnerId}
                                setValue={setPartnerId}
                                error={partnerIdError}
                            />

                            <BlockInput
                                type="text"
                                placeholder="파트너사 이름"
                                value={partnerName}
                                setValue={setPartnerName}
                                error={partnerNameError}
                            />
                        </div>
                    </div>

                    <div className={style["modal-section"]}>
                        <div className={style["section-head"]}>
                            <h5>파트너사 관리자 등록</h5>
                            <p>이 계정은 파트너 웹에 로그인할 최고 관리자 계정입니다.</p>
                        </div>
                        <ul className={style["validation-rules"]}>
                            <li>관리자 이름은 2자 이상이어야 합니다.</li>
                            <li>관리자 이메일은 이메일 형식이어야 하며 기존 관리자 이메일과 중복될 수 없습니다.</li>
                            <li>관리자 비밀번호는 6자 이상이어야 하고, 확인 값과 같아야 합니다.</li>
                        </ul>
                        <div className={style["unit--overlay--inputs"]}>
                            <BlockInput
                                type="text"
                                placeholder="관리자 이름"
                                value={name}
                                setValue={setName}
                                error={nameError}
                            />
                            <div className={style["email-check-row"]}>
                                <BlockInput
                                    type="text"
                                    placeholder="관리자 이메일"
                                    value={email}
                                    setValue={setEmail}
                                    error={emailError}
                                />
                                <button
                                    type="button"
                                    className={[style["button--secondary"], style["email-check-button"]].join(" ")}
                                    onClick={checkEmail}
                                    disabled={emailCheckStatus === "checking" || !email || Boolean(emailError)}
                                >
                                    {emailCheckStatus === "checking" ? "확인 중" : "중복체크"}
                                </button>
                            </div>
                            {emailCheckMessage && (
                                <p
                                    className={[
                                        style["field-message"],
                                        emailCheckStatus === "available" && style["field-message--success"],
                                        emailCheckStatus !== "available" && style["field-message--error"],
                                    ].join(" ")}
                                >
                                    {emailCheckMessage}
                                </p>
                            )}
                            <BlockInput
                                type={showPassword ? "text" : "password"}
                                placeholder="관리자 비밀번호"
                                value={password}
                                setValue={setPassword}
                                error={passwordError}
                                rightElement={
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword((prev) => !prev)}
                                        aria-label={showPassword ? "비밀번호 숨기기" : "비밀번호 보기"}
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                }
                            />
                            <BlockInput
                                type={showConfirm ? "text" : "password"}
                                placeholder="관리자 비밀번호 확인"
                                value={confirm}
                                setValue={setConfirm}
                                error={confirmError}
                                rightElement={
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirm((prev) => !prev)}
                                        aria-label={showConfirm ? "비밀번호 확인 숨기기" : "비밀번호 확인 보기"}
                                    >
                                        {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                }
                            />
                        </div>
                    </div>
                </div>
                <div className={[common["wrapper--overlay--footer"], style["modal-footer"]].join(" ")}>
                    <button className={style["button--secondary"]} onClick={hide}>
                        취소
                    </button>
                    <button className={style["button--primary"]} onClick={createPartner}>
                        {loading ? "등록 중" : "등록"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const PartnerDetail = ({ partner, close }: { partner: PartnerType; close: () => void }) => {
    const [deleteBtn, setDeleteBtn] = useState<"Delete" | "DeleteFill">("Delete");
    const [loadingDelete, setLoadingDelete] = useState<boolean>(false);

    const deletePartner = () => {
        if (deleteBtn === "Delete") {
            setDeleteBtn("DeleteFill");
            return;
        }

        setLoadingDelete(true);

        return httpsCallable(
            functions,
            "partnerDelete"
        )({
            docId: partner.docId,
        })
            .then((result) => {
                setLoadingDelete(false);
                setDeleteBtn("Delete");

                const data = result.data as {
                    code?: string;
                    message?: string;
                };

                if (data.code) {
                    hide();
                }
            })
            .catch((err) => {
                console.error(err);
                setLoadingDelete(false);
                setDeleteBtn("Delete");

                if (ErrMsg[err.message]) {
                    alert(ErrMsg[err.message]);
                } else {
                    alert("에러가 발생했습니다");
                }

                return;
            });
    };

    const [active, setActive] = useState<boolean>(false);
    useEffect(() => {
        setActive(true);
    }, []);
    const hide = () => {
        setActive(false);
        setTimeout(() => {
            close();
        }, 520);
    };

    return (
        <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
            <div className={[common["unit--overlay"], style["modal"], active && common["unit--overlay--active"]].join(" ")}>
                <div className={style["modal-header"]}>
                    <div>
                        <p className={style["section-kicker"]}>파트너사 상세</p>
                        <h4 className={style["modal-title"]}>{partner.name}</h4>
                        <p className={style["modal-copy"]}>{partner.id}</p>
                    </div>
                    <button className={style["icon-button"]} onClick={hide} aria-label="닫기">
                        ×
                    </button>
                </div>

                <div className={common["wrapper--overlay"]}>
                    <div className={style["modal-section"]}>
                        <div className={style["section-head"]}>
                            <h5>기본 정보</h5>
                            <p>현재 등록된 파트너사 정보입니다.</p>
                        </div>
                        <div className={style["detail-grid"]}>
                            <InfoItem label="파트너사명" value={partner.name} />
                            <InfoItem label="파트너사 ID" value={partner.id} />
                            <InfoItem label="문서 ID" value={partner.docId} />
                            <InfoItem label="등록일" value={formatDate(partner.timestamp)} />
                        </div>
                    </div>

                    <div className={[style["modal-section"], style["danger-zone"]].join(" ")}>
                        <div className={style["section-head"]}>
                            <h5>파트너사 삭제</h5>
                            <p>삭제 전 연결된 회원, 공지사항, 포인트 데이터 영향을 확인해야 합니다.</p>
                        </div>
                        <button
                            className={[
                                style["button--danger"],
                                deleteBtn === "DeleteFill" && style["button--danger-confirm"],
                            ].join(" ")}
                            onClick={deletePartner}
                        >
                            {loadingDelete ? "삭제 중" : deleteBtn === "Delete" ? "삭제하기" : "삭제 확인하기"}
                        </button>
                    </div>
                </div>
                <div className={[common["wrapper--overlay--footer"], style["modal-footer"]].join(" ")}>
                    <button className={style["button--secondary"]} onClick={hide}>
                        닫기
                    </button>
                </div>
            </div>
        </div>
    );
};

const PartnerTable = ({ openCreate }: { openCreate: () => void }) => {
    const { partner } = usePartner();
    const [selected, setSelected] = useState<PartnerType | null>(null);
    const [search, setSearch] = useState<string>("");

    const filteredPartners = partner.filter((p) => {
        const keyword = search.toLowerCase();
        return search === "" || p.id.toLowerCase().includes(keyword) || p.name.toLowerCase().includes(keyword);
    });

    return (
        <section className={style["partner-panel"]}>
            <div className={style["panel-header"]}>
                <div>
                    <p className={style["section-kicker"]}>파트너사 목록</p>
                    <h5 className={style["panel-title"]}>등록된 파트너사</h5>
                    <p className={style["panel-copy"]}>
                        총 {partner.length}개 중 {filteredPartners.length}개 표시
                    </p>
                </div>
                <button className={style["button--primary"]} onClick={openCreate}>
                    파트너사 추가
                </button>
            </div>
            <div className={style["toolbar"]}>
                <input
                    className={common["input--kiosk-filter"]}
                    type="text"
                    placeholder="파트너사명 또는 아이디로 검색"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>
            <div className={style["wrapper--partner-list"]}>
                <div className={[style["partner-row"], style["partner-row--head"]].join(" ")}>
                    <p>파트너사명</p>
                    <p>파트너사 ID</p>
                    <p>등록일</p>
                    <p>관리</p>
                </div>
                {filteredPartners.length === 0 && <div className={style["empty-state"]}>검색 결과가 없습니다.</div>}
                {filteredPartners.map((p) => (
                    <PartnerList key={p.docId || p.id} partner={p} setPartner={() => setSelected(p)} />
                ))}
            </div>

            {selected && <PartnerDetail close={() => setSelected(null)} partner={selected} />}
        </section>
    );
};

const PartnerList = ({ partner, setPartner }: { partner: PartnerType; setPartner: () => void }) => {
    return (
        <div className={style["unit--partner-list"]} onClick={setPartner}>
            <div className={style["partner-row"]}>
                <p className={style["partner-name"]}>{partner.name}</p>
                <p className={style["partner-id"]}>{partner.id}</p>
                <p className={style["partner-id"]}>{formatDate(partner.timestamp)}</p>
                <div className={style["row-actions"]}>
                    <button className={style["button--ghost"]}>상세</button>
                </div>
            </div>
        </div>
    );
};

const InfoItem = ({ label, value }: { label: string; value: string }) => (
    <div className={style["info-item"]}>
        <span>{label}</span>
        <strong>{value}</strong>
    </div>
);

const formatDate = (timestamp?: number) => {
    if (!timestamp) return "-";
    return new Date(timestamp).toLocaleDateString("ko-KR");
};

export default Partner;
